import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./Cards.css";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import { supabase } from "../supabaseClient";
import Loading from "./Loading";
import ModalNota from "./ModalNota";
import ListagemEspelho from "./ListagemEspelho";
import Column from "./CardsColumn";
import CardsHeader from "./CardsHeader";
import CardsTopBar from "./CardsTopBar";
import CardsGraficos from "./CardsGraficos";
import EntityDetails from "./EntityDetails";
import ProjetosObra from "./ProjetosObra";
import { registrarAtividade } from "../utils/registrarAtividade";
import TrocarProjetoModal from "./TrocarProjetoModal";
import NotaRapidaCard from "./NotaRapidaCard";
import TempoTracker from "./TempoTracker";

const TIPOS_COM_VISUALIZACAO = [
  "Atas", "Lista", "Metas", "Tarefas", "Diário de Obra",
];

export default function Cards({ session, profile, onLogout, onProfileUpdate }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [entity, setEntity]                     = useState(null);
  const [columnsNormais, setColumnsNormais]     = useState([]);
  const [columnsArquivadas, setColumnsArquivadas] = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [entityType, setEntityType]             = useState("project");
  const [viewMode, setViewMode]                 = useState("kanban");
  const [activeColumnId, setActiveColumnId]     = useState(null);
  const [editingColumnId, setEditingColumnId]   = useState(null);
  const [columnTitleDraft, setColumnTitleDraft] = useState("");
  const [formData, setFormData]                 = useState({ nome: "", responsavel: "", tipo: "" });
  const [notaEditData, setNotaEditData]         = useState({ id: null, nome: "", responsavel: "", pilhaId: null });
  const [notaSelecionada, setNotaSelecionada]   = useState(null);
  const [notaHistorico, setNotaHistorico]       = useState([]);
  const [usuarioAtual, setUsuarioAtual]         = useState("Usuário Atual");
  const [usuarioId, setUsuarioId]               = useState(null);
  const [notaProgresso, setNotaProgresso]       = useState({});
  const [menuOpenNota, setMenuOpenNota]         = useState(null);
  const [menuOpenPilha, setMenuOpenPilha]       = useState(null);
  const [projetoOrigem, setProjetoOrigem]       = useState(null);
  const [notaOrigem, setNotaOrigem]             = useState(null);
  const [donoContainerId, setDonoContainerId]   = useState(null);
  const [isNotaRecebidos, setIsNotaRecebidos]   = useState(false);
  const [showColorPicker, setShowColorPicker]   = useState({});
  const [notasConcluidas, setNotasConcluidas]   = useState(new Set());
  const [dataConclusaoEdit, setDataConclusaoEdit] = useState({});
  const [dataConclusaoSalva, setDataConclusaoSalva] = useState({});
  const [membros, setMembros]                   = useState([]);
  const [searchTerm, setSearchTerm]             = useState("");
  const [dataEntregaEdit, setDataEntregaEdit]   = useState({});
  const [dataEntregaSalva, setDataEntregaSalva] = useState({});
  const [expandedColumnId, setExpandedColumnId] = useState(null);
  const [expandedNotaView, setExpandedNotaView] = useState(null);
  const [filtroPrioridade, setFiltroPrioridade] = useState(null);
  const [filtroTag, setFiltroTag]               = useState(null);
  const [pilhaSelecionada, setPilhaSelecionada] = useState(null);
  const [showTrocarProjeto, setShowTrocarProjeto] = useState(false);

  // ── Estado das tags da entidade ──
  const [tags, setTags] = useState([]);

  // ── Estados da Pilha Compartilhada ──
  const [showCompartilhadaModal, setShowCompartilhadaModal] = useState(false);
  const [entidadesParaCompartilhar, setEntidadesParaCompartilhar] = useState([]);
  const [selecionadasCompartilhar, setSelecionadasCompartilhar] = useState([]);
  const [carregandoEntidadesCompartilhar, setCarregandoEntidadesCompartilhar] = useState(false);

  // ── Estados do Gerenciar Compartilhamento ──
  const [showGerenciarModal, setShowGerenciarModal] = useState(false);
  const [pilhaGerenciada, setPilhaGerenciada] = useState(null);
  const [selecionadasGerenciar, setSelecionadasGerenciar] = useState([]);
  const [salvandoGerenciar, setSalvandoGerenciar] = useState(false);

  const modoArquivadas = viewMode === "arquivadas";
  const modoGraficos   = viewMode === "graficos";
  const modoProjetos   = viewMode === "projetos";
  const modoHome       = viewMode === "home";

  const columnsAtivas = modoArquivadas ? columnsArquivadas : columnsNormais;

  const atualizarStatusNota = (notaId, updates) => {
    const setter = modoArquivadas ? setColumnsArquivadas : setColumnsNormais;
    setter(prev => prev.map(col => ({
      ...col,
      notas: col.notas.map(nota => nota.id === notaId ? { ...nota, ...updates } : nota),
    })));
  };

  const updateUrlWithNota = (notaId) => {
    const urlParams = new URLSearchParams(location.search);
    if (notaId) urlParams.set("nota", notaId);
    else urlParams.delete("nota");
    navigate(`${location.pathname}?${urlParams.toString()}`, { replace: false });
  };

  const abrirNotaComEstado = (nota) => {
    updateUrlWithNota(nota.id);
    setNotaSelecionada(nota);
    let isRecebidos = false;
    for (const col of columnsAtivas) {
      if (col.notas.some(n => n.id === nota.id)) {
        isRecebidos = col.title === "Recebidos";
        break;
      }
    }
    setIsNotaRecebidos(isRecebidos);
    if (isRecebidos) loadOrigemData(nota.id);
    else {
      setProjetoOrigem(null);
      setNotaOrigem(null);
    }
  };

  const handleOpenNota = (nota) => {
    if (nota.tipo === "Nota Rápida") return;
    setNotaHistorico([]);
    abrirNotaComEstado(nota);
  };

  const handleCloseNota = () => {
    if (notaHistorico.length > 0) {
      const anterior = notaHistorico[notaHistorico.length - 1];
      setNotaHistorico(prev => prev.slice(0, -1));
      abrirNotaComEstado(anterior);
      return;
    }
    setNotaSelecionada(null);
    setIsNotaRecebidos(false);
    setProjetoOrigem(null);
    setNotaOrigem(null);
    updateUrlWithNota(null);
  };

  const handleOpenTask = useCallback(async (notaId) => {
    if (notaSelecionada && String(notaSelecionada.id) !== String(notaId)) {
      setNotaHistorico(prev => [...prev, notaSelecionada]);
    }

    const todasNotas = [...columnsNormais, ...columnsArquivadas].flatMap(c => c.notas);
    let notaAlvo = todasNotas.find(n => String(n.id) === String(notaId));

    if (!notaAlvo) {
      const { data } = await supabase.from("notas").select("*").eq("id", notaId).single();
      if (data) {
        setColumnsNormais(prev => prev.map(c =>
          String(c.id) === String(data.pilha_id)
            ? { ...c, notas: [...c.notas.filter(n => n.id !== data.id), data] }
            : c
        ));
        notaAlvo = data;
      }
    }

    if (notaAlvo) abrirNotaComEstado(notaAlvo);
  }, [columnsNormais, columnsArquivadas, notaSelecionada, columnsAtivas]);

  const handleSaveNomeRapida = async (notaId, novoNome) => {
    if (!novoNome.trim()) return;
    const { error } = await supabase.from("notas").update({ nome: novoNome.trim() }).eq("id", notaId);
    if (!error) atualizarStatusNota(notaId, { nome: novoNome.trim() });
  };

  const handleSaveResponsavelRapida = async (notaId, userId) => {
    if (!userId) return;
    const { data: notaAtual, error: fetchError } = await supabase.from("notas").select("responsaveis_ids").eq("id", notaId).single();
    if (fetchError) { alert("Erro ao atualizar responsável."); return; }
    const idsExistentes = notaAtual.responsaveis_ids || [];
    const jaTem = idsExistentes.includes(userId);
    const novosIds = jaTem ? idsExistentes.filter(id => id !== userId) : [...idsExistentes, userId];
    const { error } = await supabase.from("notas").update({ responsaveis_ids: novosIds }).eq("id", notaId);
    if (!error) atualizarStatusNota(notaId, { responsaveis_ids: novosIds });
    else alert("Erro ao salvar responsável.");
  };

  const handleSaveDataEntregaRapida = async (notaId, data) => {
    const { error } = await supabase.from("notas").update({ data_entrega: data || null }).eq("id", notaId);
    if (!error) atualizarStatusNota(notaId, { data_entrega: data || null });
  };

  const handleOpenAgendaItem = (item) => {
  const container_id = item._containerId || donoContainerId;
  const projeto_id = item._projetoId;
  const setor_id = item._setorId;
  const pilha_id = item._pilhaId;
  const nota_id = item._notaId;

  const entityId = projeto_id || setor_id;
  if (!entityId) {
    alert("Não foi possível localizar o projeto/setor deste item.");
    return;
  }

  const queryParams = new URLSearchParams({
    entityId,
    type: setor_id ? "setor" : "project",
    containerId: container_id,
  });
  if (nota_id) queryParams.set("nota", nota_id);

  // mesmo projeto/setor: só abre a nota, sem recarregar a página
  if (String(entityId) === String(entity?.id)) {
    if (nota_id) handleOpenTask(nota_id);
    return;
  }

  navigate(`/cards/${entityId}?${queryParams.toString()}`, {
    state: {
      projectId: projeto_id,
      setorId: setor_id,
      pilhaId: pilha_id,
      notaId: nota_id,
      entityType: setor_id ? "setor" : "project",
      containerId: container_id,
      openNota: !!nota_id,
    },
  });
};

  const handleResetView = useCallback(() => {
    setViewMode("kanban");
    setFiltroPrioridade(null);
    setFiltroTag(null);
    setPilhaSelecionada(null);
  }, []);

  const handleRemoveResponsavelRapida = async (notaId, userIdToRemove = null) => {
    const { data: notaAtual, error: fetchError } = await supabase.from("notas").select("responsaveis_ids").eq("id", notaId).single();
    if (fetchError) return;
    const novosIds = userIdToRemove
      ? (notaAtual.responsaveis_ids || []).filter(id => id !== userIdToRemove)
      : [];
    const { error } = await supabase.from("notas").update({ responsaveis_ids: novosIds }).eq("id", notaId);
    if (!error) atualizarStatusNota(notaId, { responsaveis_ids: novosIds });
  };

  const handleSavePrioridade = async (notaId, prioridade) => {
    const { error } = await supabase
      .from("notas")
      .update({ prioridade: prioridade || null })
      .eq("id", notaId);
    if (!error) atualizarStatusNota(notaId, { prioridade: prioridade || null });
  };

  const handleToggleFlutuante = async (nota, pilhaId) => {
    const ativar = !nota.flutuante;
    const updates = ativar
      ? { flutuante: true, pos_x: nota.pos_x ?? 80, pos_y: nota.pos_y ?? 80, largura: nota.largura || 260, altura: nota.altura || 220 }
      : { flutuante: false };
    const { error } = await supabase.from("notas").update(updates).eq("id", nota.id);
    if (!error) atualizarStatusNota(nota.id, updates);
  };

  const handleSalvarPosicaoFlutuante = async (notaId, pos_x, pos_y) => {
    atualizarStatusNota(notaId, { pos_x, pos_y });
    await supabase.from("notas").update({ pos_x, pos_y }).eq("id", notaId);
  };

  const handleRedimensionarNotaFlutuante = async (notaId, largura, altura) => {
    atualizarStatusNota(notaId, { largura, altura });
    await supabase.from("notas").update({ largura, altura }).eq("id", notaId);
  };

  const jaExisteDiario = columnsNormais.some(col => col.tipo_pilha === "diario_obras");

  const CAMPO_ID_PILHA = { project: "project_id", setor: "setor_id", workspace: "workspace_id" };

  const handleCreateColumn = async (tipo) => {
    if (!entity) return;

    if (!donoContainerId) {
      console.error("donoContainerId está vazio ao criar pilha — a pilha vai nascer sem container_id.");
      alert("Não foi possível identificar o container. Recarregue a página e tente novamente.");
      return;
    }

    if (tipo === "diario_obras") {
      if (entityType === "workspace") {
        alert("Workspaces não possuem Diário de Obra.");
        return;
      }
      if (columnsNormais.some(c => c.tipo_pilha === "diario_obras")) {
        alert("Já existe uma pilha 'Diário de Obra' neste projeto/setor.");
        return;
      }
    }

    const cols = modoArquivadas ? columnsArquivadas : columnsNormais;
    const outras = cols.filter(c => c.title !== "Recebidos");
    const maxOrdem = outras.length > 0 ? Math.max(...outras.map(c => c.ordem)) : -1;
    const newOrdem = maxOrdem + 1;

    const newPilhaData = {
      title: tipo === "diario_obras" ? "Diário de Obra" : "Nova Pilha",
      ordem: newOrdem, arquivada: modoArquivadas,
      tipo_pilha: tipo === "diario_obras" ? "diario_obras" : null,
      container_id: donoContainerId,
      [CAMPO_ID_PILHA[entityType] || "setor_id"]: entity.id,
    };

    const { data: newPilha, error } = await supabase.from("pilhas").insert([newPilhaData]).select().single();

    if (error) {
      console.error("Erro ao criar pilha:", error);
      alert(`Erro ao criar pilha: ${error.message}`);
      return;
    }

    const novaCol = { id: String(newPilha.id), title: newPilha.title, notas: [], cor_fundo: null, ordem: newOrdem, arquivada: modoArquivadas, tipo_pilha: newPilha.tipo_pilha || null };
    if (modoArquivadas) setColumnsArquivadas(prev => [...prev, novaCol]);
    else setColumnsNormais(prev => [...prev, novaCol]);
  };

  // ── Carrega entidades do mesmo container para compartilhar ──
  const carregarEntidadesParaCompartilhar = async () => {
    if (!donoContainerId) return;
    setCarregandoEntidadesCompartilhar(true);
    try {
      const [{ data: projetos }, { data: setores }, { data: workspaces }] = await Promise.all([
        supabase.from("projects").select("id, name").eq("user_id", donoContainerId),
        supabase.from("setores").select("id, name").eq("user_id", donoContainerId),
        supabase.from("workspaces").select("id, name").eq("user_id", donoContainerId),
      ]);
      const lista = [
        ...(projetos || []).map(p => ({ id: p.id, nome: p.name, tipo: "project" })),
        ...(setores || []).map(s => ({ id: s.id, nome: s.name, tipo: "setor" })),
        ...(workspaces || []).map(w => ({ id: w.id, nome: w.name, tipo: "workspace" })),
      ].filter(e => !(e.tipo === entityType && String(e.id) === String(entity?.id)));
      setEntidadesParaCompartilhar(lista);
    } catch (err) {
      console.error("Erro ao carregar entidades para compartilhar:", err);
    } finally {
      setCarregandoEntidadesCompartilhar(false);
    }
  };

 const handleCreateColumnCompartilhada = async () => {
  if (!entity) { alert("Entidade não carregada. Recarregue a página."); return; }
  if (!donoContainerId) {
    alert("Não foi possível identificar o container. Recarregue a página e tente novamente.");
    return;
  }
  if (selecionadasCompartilhar.length === 0) {
    alert("Selecione ao menos um projeto, setor ou workspace para compartilhar.");
    return;
  }

  const cols = modoArquivadas ? columnsArquivadas : columnsNormais;
  const outras = cols.filter(c => c.title !== "Recebidos");
  const maxOrdem = outras.length > 0 ? Math.max(...outras.map(c => c.ordem)) : -1;
  const newOrdem = maxOrdem + 1;

  const payloadPilha = {
    title: "Pilha Compartilhada",
    ordem: newOrdem,
    arquivada: modoArquivadas,
    tipo_pilha: "compartilhada",
    container_id: donoContainerId,
  };

  const { data: newPilha, error } = await supabase.from("pilhas").insert([payloadPilha]).select().single();

  if (error) {
    console.error("Erro ao criar pilha compartilhada:", error);
    alert(`Erro ao criar pilha compartilhada: ${error.message}`);
    return;
  }

  // ── Verificação extra: garante que o banco realmente gravou o container_id ──
  if (!newPilha.container_id) {
    console.error("Pilha criada sem container_id! payload enviado:", payloadPilha, "retorno:", newPilha);
    alert("Erro: a pilha foi criada mas sem container_id. Contate o suporte antes de usá-la (isso não deveria acontecer).");
    // Apaga a pilha órfã para não deixar lixo no banco
    await supabase.from("pilhas").delete().eq("id", newPilha.id);
    return;
  }

  const vinculos = [
    { pilha_id: newPilha.id, entidade_tipo: entityType, entidade_id: entity.id, container_id: donoContainerId },
    ...selecionadasCompartilhar.map(s => ({
      pilha_id: newPilha.id, entidade_tipo: s.tipo, entidade_id: s.id, container_id: donoContainerId,
    })),
  ];
  const { error: errVinculo } = await supabase.from("pilhas_compartilhamentos").insert(vinculos);
  if (errVinculo) {
    console.error("Erro ao vincular entidades:", errVinculo);
    alert("A pilha foi criada, mas houve um erro ao vincular alguns destinos.");
  }

  const novaCol = {
    id: String(newPilha.id), title: newPilha.title, notas: [], cor_fundo: null,
    ordem: newOrdem, arquivada: modoArquivadas, tipo_pilha: "compartilhada",
    compartilhada: true,
    entidadesCompartilhadas: [
            { tipo: entityType, id: entity.id, nome: entity.name || entity.nome || "Este" },
      ...selecionadasCompartilhar,
    ],
  };
  if (modoArquivadas) setColumnsArquivadas(prev => [...prev, novaCol]);
  else setColumnsNormais(prev => [...prev, novaCol]);

  setShowCompartilhadaModal(false);
  setSelecionadasCompartilhar([]);
};

  // ── Gerenciar compartilhamento (abrir modal + salvar diff) ──
  const abrirGerenciarCompartilhamento = async (col) => {
    setPilhaGerenciada(col);
    setSelecionadasGerenciar(
      (col.entidadesCompartilhadas || []).filter(
        e => !(e.tipo === entityType && String(e.id) === String(entity?.id))
      )
    );
    setShowGerenciarModal(true);
    await carregarEntidadesParaCompartilhar();
  };

  const salvarGerenciarCompartilhamento = async () => {
    const col = pilhaGerenciada;
    if (!col || !entity) return;
    setSalvandoGerenciar(true);

    const chave = e => `${e.tipo}-${e.id}`;
    const universo = new Set(entidadesParaCompartilhar.map(chave));
    const atuais = (col.entidadesCompartilhadas || []).filter(
      e => !(e.tipo === entityType && String(e.id) === String(entity.id))
    );
    const atuaisNaLista = atuais.filter(e => universo.has(chave(e)));
    const atuaisChaves = new Set(atuaisNaLista.map(chave));
    const novasChaves = new Set(selecionadasGerenciar.map(chave));
    const adicionar = selecionadasGerenciar.filter(e => !atuaisChaves.has(chave(e)));
    const remover = atuaisNaLista.filter(e => !novasChaves.has(chave(e)));

    try {
      if (adicionar.length > 0) {
        const { error } = await supabase.from("pilhas_compartilhamentos").insert(
          adicionar.map(e => ({
            pilha_id: col.id,
            entidade_tipo: e.tipo,
            entidade_id: e.id,
            container_id: donoContainerId,
          }))
        );
        if (error) throw error;
      }

      for (const e of remover) {
        const { error } = await supabase
          .from("pilhas_compartilhamentos")
          .delete()
          .eq("pilha_id", col.id)
          .eq("entidade_tipo", e.tipo)
          .eq("entidade_id", e.id);
        if (error) throw error;
      }

      const entidadesFinal = [
        { tipo: entityType, id: entity.id, nome: entity.name || entity.nome || "Este" },
        ...atuais.filter(e => !universo.has(chave(e))),
        ...selecionadasGerenciar,
      ];

      const aplicar = (setter) => setter(prev =>
        prev.map(c => c.id === col.id ? { ...c, entidadesCompartilhadas: entidadesFinal } : c)
      );
      aplicar(setColumnsNormais);
      aplicar(setColumnsArquivadas);

      setShowGerenciarModal(false);
      setPilhaGerenciada(null);
      setSelecionadasGerenciar([]);
    } catch (err) {
      console.error("Erro ao atualizar compartilhamento:", err);
      alert(`Erro ao atualizar compartilhamento: ${err.message}`);
    } finally {
      setSalvandoGerenciar(false);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      const urlParams = new URLSearchParams(location.search);
      const entityIdFromUrl = urlParams.get("entityId");
      const entityTypeFromUrl = urlParams.get("type");
      const containerIdFromUrl = urlParams.get("containerId");
      const notaIdFromUrl = urlParams.get("nota");
      const { projectId, setorId, entityType: typeFromState, containerId: containerIdFromState } = location.state || {};
      const entityId = entityIdFromUrl || projectId || setorId;
      const type = entityTypeFromUrl || typeFromState || (projectId ? "project" : "setor");
      const containerId = containerIdFromUrl || containerIdFromState;

      if (!entityId) { navigate("/containers", { replace: true }); setLoading(false); return; }
      if (containerId) setDonoContainerId(containerId);

      if (!entityIdFromUrl || !entityTypeFromUrl) {
        const newUrl = new URLSearchParams();
        newUrl.set("entityId", entityId); newUrl.set("type", type);
        if (containerId) newUrl.set("containerId", containerId);
        if (notaIdFromUrl) newUrl.set("nota", notaIdFromUrl);
        navigate(`${location.pathname}?${newUrl.toString()}`, { replace: true, state: location.state });
      }

      const TABELA_ENTIDADE = { project: "projects", setor: "setores", workspace: "workspaces" };
      const TABELA_MEMBROS  = { project: "project_members", setor: "setor_members", workspace: "workspace_members" };
      const CAMPO_ID        = { project: "project_id", setor: "setor_id", workspace: "workspace_id" };

      setEntityType(type); setLoading(true);

      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        const currentUserId = s?.user?.id;
        setUsuarioId(currentUserId);
        if (currentUserId) {
          const { data: profileData } = await supabase.from("profiles").select("nome").eq("id", currentUserId).single();
          if (profileData?.nome) setUsuarioAtual(profileData.nome);
        }

        const { data: entityData, error: entityError } = await supabase
          .from(TABELA_ENTIDADE[type] || "setores")
          .select("*")
          .eq("id", entityId)
          .single();

        if (entityError) {
          const label = type === "project" ? "Projeto" : type === "workspace" ? "Workspace" : "Setor";
          throw new Error(`${label} não encontrado`);
        }

        const memberTable = TABELA_MEMBROS[type] || "setor_members";
        const idField = CAMPO_ID[type] || "setor_id";
        const { data: membersData } = await supabase.from(memberTable).select("user_id").eq(idField, entityId);
        if (membersData?.length > 0) {
          const userIds = membersData.map(m => m.user_id);
          const { data: perfis } = await supabase.from("profiles").select("id, nickname, avatar_url").in("id", userIds);
          setMembros(perfis || []);
        }

        const { data: pilhasNormaisNull } = await supabase.from("pilhas").select("*").eq(idField, entityId).is("arquivada", null).order("ordem", { ascending: true });
        const { data: pilhasNormaisFalse } = await supabase.from("pilhas").select("*").eq(idField, entityId).eq("arquivada", false).order("ordem", { ascending: true });
        const { data: pilhasArquivadasRaw } = await supabase.from("pilhas").select("*").eq(idField, entityId).eq("arquivada", true).order("ordem", { ascending: true });

        const pilhasNormais = [...(Array.isArray(pilhasNormaisNull) ? pilhasNormaisNull : []), ...(Array.isArray(pilhasNormaisFalse) ? pilhasNormaisFalse : [])];
        const pilhasArquivadas = Array.isArray(pilhasArquivadasRaw) ? pilhasArquivadasRaw : [];

        const pilhasNormaisFiltradas = pilhasNormais.filter(p => p.tipo_pilha !== "sistema_calendario");
        const pilhasArquivadasFiltradas = pilhasArquivadas.filter(p => p.tipo_pilha !== "sistema_calendario");

        // ── Pilhas compartilhadas com esta entidade ──
        const { data: compartilhamentos } = await supabase
          .from("pilhas_compartilhamentos")
          .select("pilha_id")
          .eq("entidade_tipo", type)
          .eq("entidade_id", entityId);

        const pilhaIdsCompartilhadas = [...new Set((compartilhamentos || []).map(c => c.pilha_id))];

        let pilhasCompartilhadasRaw = [];
        let entidadesPorPilha = {};
        if (pilhaIdsCompartilhadas.length > 0) {
          const { data: pilhasComp } = await supabase.from("pilhas").select("*").in("id", pilhaIdsCompartilhadas);
          pilhasCompartilhadasRaw = pilhasComp || [];

          const { data: todosVinculos } = await supabase
            .from("pilhas_compartilhamentos")
            .select("pilha_id, entidade_tipo, entidade_id")
            .in("pilha_id", pilhaIdsCompartilhadas);

          const idsPorTipo = { project: [], setor: [], workspace: [] };
          (todosVinculos || []).forEach(v => idsPorTipo[v.entidade_tipo]?.push(v.entidade_id));

          const [{ data: projs }, { data: sets }, { data: works }] = await Promise.all([
            idsPorTipo.project.length ? supabase.from("projects").select("id, name").in("id", idsPorTipo.project) : Promise.resolve({ data: [] }),
            idsPorTipo.setor.length ? supabase.from("setores").select("id, name").in("id", idsPorTipo.setor) : Promise.resolve({ data: [] }),
            idsPorTipo.workspace.length ? supabase.from("workspaces").select("id, name").in("id", idsPorTipo.workspace) : Promise.resolve({ data: [] }),
          ]);
          const nomesMap = {};
          (projs || []).forEach(p => nomesMap[`project-${p.id}`] = p.name);
          (sets || []).forEach(s => nomesMap[`setor-${s.id}`] = s.name);
          (works || []).forEach(w => nomesMap[`workspace-${w.id}`] = w.name);

          (todosVinculos || []).forEach(v => {
            if (!entidadesPorPilha[v.pilha_id]) entidadesPorPilha[v.pilha_id] = [];
            entidadesPorPilha[v.pilha_id].push({ tipo: v.entidade_tipo, id: v.entidade_id, nome: nomesMap[`${v.entidade_tipo}-${v.entidade_id}`] || "—" });
          });
        }

        const pilhasCompNormais = pilhasCompartilhadasRaw.filter(p => !p.arquivada);
        const pilhasCompArquivadas = pilhasCompartilhadasRaw.filter(p => p.arquivada);

        const fetchNotas = async (pilhas) => Promise.all(pilhas.map(async (pilha) => {
          const { data: notasRaw } = await supabase.from("notas").select("*").eq("pilha_id", pilha.id).order("ordem", { ascending: true });
          return { ...pilha, notas: Array.isArray(notasRaw) ? notasRaw : [] };
        }));

        const pilhasNormaisComNotas = await fetchNotas([...pilhasNormaisFiltradas, ...pilhasCompNormais]);
        const pilhasArquivadasComNotas = await fetchNotas([...pilhasArquivadasFiltradas, ...pilhasCompArquivadas]);

        const initEstado = (pilhas) => {
          const progresso = {}, concluidas = new Set(), dataConclusao = {}, dataEntrega = {};
          pilhas.forEach(p => p.notas.forEach(n => {
            if (n.progresso != null) progresso[n.id] = n.progresso;
            if (n.concluida) concluidas.add(String(n.id));
            if (n.data_conclusao) dataConclusao[n.id] = n.data_conclusao.split("T")[0];
            if (n.data_entrega) dataEntrega[n.id] = n.data_entrega.split("T")[0];
          }));
          return { progresso, concluidas, dataConclusao, dataEntrega };
        };

        const { progresso: progN, concluidas: concN, dataConclusao: dataN, dataEntrega: dataE } = initEstado(pilhasNormaisComNotas);
        setNotaProgresso(progN); setNotasConcluidas(concN); setDataConclusaoSalva(dataN); setDataEntregaSalva(dataE);

        const mapCol = (p, arquivada) => ({
          id: String(p.id), title: p.title, notas: p.notas, cor_fundo: p.cor_fundo || null,
          ordem: p.ordem ?? 999, arquivada, tipo_pilha: p.tipo_pilha || null, responsavel_id: p.responsavel_id || null,
          ordenacao_campo: p.ordenacao_campo || null,
          ordenacao_dir: p.ordenacao_dir || null,
          compartilhada: p.tipo_pilha === "compartilhada",
          entidadesCompartilhadas: entidadesPorPilha[p.id] || [],
        });
        const sortByOrdem = (arr) => [...arr].sort((a, b) => (a.ordem ?? 999) - (b.ordem ?? 999));
        setColumnsNormais(sortByOrdem(pilhasNormaisComNotas).map(p => mapCol(p, false)));
        setColumnsArquivadas(sortByOrdem(pilhasArquivadasComNotas).map(p => mapCol(p, true)));
        setEntity({ ...entityData, type });

        if (notaIdFromUrl) {
          const buscar = (cols) => cols.flatMap(col => col.notas).find(n => String(n.id) === notaIdFromUrl && TIPOS_COM_VISUALIZACAO.includes(n.tipo));
          const notaNormal = buscar(pilhasNormaisComNotas);
          const notaArquivada = buscar(pilhasArquivadasComNotas);
          if (notaArquivada) {
            setViewMode("arquivadas");
            setNotaSelecionada(notaArquivada);
            const col = pilhasArquivadasComNotas.find(c => c.notas.some(n => n.id === notaArquivada.id));
            if (col?.title === "Recebidos") { setIsNotaRecebidos(true); loadOrigemData(notaArquivada.id); }
          } else if (notaNormal) {
            setNotaSelecionada(notaNormal);
            const col = pilhasNormaisComNotas.find(c => c.notas.some(n => n.id === notaNormal.id));
            if (col?.title === "Recebidos") { setIsNotaRecebidos(true); loadOrigemData(notaNormal.id); }
          }
        }
      } catch (err) {
        alert("Erro ao carregar dados. Redirecionando...");
        navigate("/containers", { replace: true });
      } finally { setLoading(false); }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const notaId = urlParams.get("nota");
    if (notaSelecionada && notaId && String(notaSelecionada.id) === notaId) return;
    if (!notaId && notaSelecionada) {
      const timer = setTimeout(() => {
        const check = new URLSearchParams(window.location.search).get("nota");
        if (!check && notaSelecionada) { setNotaSelecionada(null); setIsNotaRecebidos(false); setProjetoOrigem(null); setNotaOrigem(null); }
      }, 200);
      return () => clearTimeout(timer);
    }
    if (notaId && columnsAtivas.length > 0) {
      let found = false;
      for (const col of columnsAtivas) {
        const nota = col.notas.find(n => String(n.id) === notaId && TIPOS_COM_VISUALIZACAO.includes(n.tipo));
        if (nota) {
          setNotaSelecionada(nota);
          const isRecebidos = col.title === "Recebidos";
          setIsNotaRecebidos(isRecebidos);
          if (isRecebidos) loadOrigemData(nota.id);
          found = true; break;
        }
      }
      if (!found && (!notaSelecionada || String(notaSelecionada.id) !== notaId)) {
        supabase.from("notas").select("*").eq("id", notaId).single()
          .then(({ data: nota, error }) => {
            if (!error && nota && TIPOS_COM_VISUALIZACAO.includes(nota.tipo)) { setNotaSelecionada(nota); setIsNotaRecebidos(false); }
          });
      }
    }
  }, [columnsAtivas, location.search]);

  const loadOrigemData = async (notaEspelhoId) => {
    try {
      const { data: notaEspelho } = await supabase.from("notas").select("projeto_origem_id, nota_original_id, nome").eq("id", notaEspelhoId).single();
      if (notaEspelho?.projeto_origem_id) {
        const { data: projeto } = await supabase.from("projects").select("id, name").eq("id", notaEspelho.projeto_origem_id).single();
        setProjetoOrigem(projeto || null);
      }
      if (notaEspelho?.nota_original_id) {
        const { data: nota } = await supabase.from("notas").select("id, nome").eq("id", notaEspelho.nota_original_id).single();
        setNotaOrigem(nota || null);
      } else {
        setNotaOrigem({ id: notaEspelhoId, nome: notaEspelho?.nome || "Sem nome" });
      }
    } catch { setProjetoOrigem(null); setNotaOrigem(null); }
  };

  useEffect(() => {
    const fn = (e) => {
      if (menuOpenNota &&
        !e.target.closest(".card-menu-dropdown") &&
        !e.target.closest(".card-menu-btn") &&
        !e.target.closest(".kcard__dropdown") &&
        !e.target.closest(".kcard__menu-btn") &&
        !e.target.closest(".nrc-menu-dropdown") &&
        !e.target.closest(".nrc-menu-btn")
      ) setMenuOpenNota(null);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [menuOpenNota]);

  useEffect(() => {
    const fn = (e) => { if (menuOpenPilha && !e.target.closest(".card-menu-dropdown") && !e.target.closest(".column-menu-btn")) setMenuOpenPilha(null); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [menuOpenPilha]);

  useEffect(() => {
    const fn = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || notaSelecionada || activeColumnId || notaEditData.id) return;
      const cardsBody = document.querySelector(".cards-body");
      if (!cardsBody) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); cardsBody.scrollBy({ left: -300, behavior: "smooth" }); }
      if (e.key === "ArrowRight") { e.preventDefault(); cardsBody.scrollBy({ left: 300, behavior: "smooth" }); }
    };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [notaSelecionada, activeColumnId, notaEditData.id]);

  const toggleConclusaoNota = async (notaId, concluida) => {
    const newConcluida = !concluida;
    const { error } = await supabase.from("notas").update({ concluida: newConcluida }).eq("id", notaId);
    if (!error) {
      setNotasConcluidas(prev => { const novo = new Set(prev); newConcluida ? novo.add(String(notaId)) : novo.delete(String(notaId)); return novo; });

      if (newConcluida) {
        const notaEncontrada = [...columnsNormais, ...columnsArquivadas]
          .flatMap(c => c.notas).find(n => n.id === notaId);
        await registrarAtividade({
          projectId:  entity?.id,
          userId:     usuarioId,
          tipoAcao:   "concluiu_tarefa",
          entidade:   "nota",
          entidadeId: notaId,
          nomeItem:   notaEncontrada?.nome || null,
        });
      }
    }
  };

  const handleSaveTask = async () => {
    if (!formData.nome.trim() || !activeColumnId) return;
    if (!usuarioId) return alert("Você precisa estar logado.");

    try {
      const { nome, tipo } = formData;

      const { data: newNota, error: notaError } = await supabase
        .from("notas")
        .insert([{
          nome,
          tipo,
          pilha_id: activeColumnId,
          responsavel: usuarioId,
          concluida: false,
          ordem: 0
        }])
        .select()
        .single();

      if (notaError) {
        console.error("Erro ao criar nota:", notaError);
        alert(`Erro ao criar nota: ${notaError.message}`);
        return;
      }

      await registrarAtividade({
        projectId:  entity?.id,
        userId:     usuarioId,
        tipoAcao:   "criou_nota",
        entidade:   "nota",
        entidadeId: newNota.id,
        nomeItem:   newNota.nome,
      });

      const setter = modoArquivadas ? setColumnsArquivadas : setColumnsNormais;
      setter(prev => prev.map(c =>
        c.id === activeColumnId
          ? { ...c, notas: [newNota, ...c.notas] }
          : c
      ));

      if (TIPOS_COM_VISUALIZACAO.includes(newNota.tipo)) {
        setNotaSelecionada(newNota);
        updateUrlWithNota(newNota.id);
        setIsNotaRecebidos(false);
        setProjetoOrigem(null);
        setNotaOrigem(null);
      }

      setFormData({ nome: "", responsavel: "", tipo: "Lista" });
      setActiveColumnId(null);

    } catch (err) {
      console.error("Erro ao criar nota:", err);
      alert(`Erro ao criar nota: ${err.message || "Erro desconhecido"}`);
    }
  };

  const handleDeleteNota = async (notaId, pilhaId) => {
    if (!window.confirm("Excluir esta nota?")) return;
    const setter = modoArquivadas ? setColumnsArquivadas : setColumnsNormais;
    setter(prev => prev.map(c => c.id === pilhaId ? { ...c, notas: c.notas.filter(n => n.id !== notaId) } : c));
    setMenuOpenNota(null);
    setNotaProgresso(prev => { const u = { ...prev }; delete u[notaId]; return u; });
    setNotasConcluidas(prev => { const u = new Set(prev); u.delete(String(notaId)); return u; });
    if (notaSelecionada?.id === notaId) { setNotaSelecionada(null); setIsNotaRecebidos(false); setProjetoOrigem(null); setNotaOrigem(null); updateUrlWithNota(null); }
    await supabase.from("notas").delete().eq("id", notaId);
  };

  const handleEditNota = (nota, pilhaId) => setNotaEditData({ id: nota.id, nome: nota.nome, responsavel: nota.responsavel || "", pilhaId });

  const saveEditedNota = async () => {
    const { id, nome, responsavel, pilhaId } = notaEditData;
    if (!nome.trim()) return alert("Digite o nome da nota!");
    const { error } = await supabase.from("notas").update({ nome, responsavel }).eq("id", id);
    if (!error) {
      const setter = modoArquivadas ? setColumnsArquivadas : setColumnsNormais;
      setter(prev => prev.map(c => c.id === pilhaId ? { ...c, notas: c.notas.map(n => n.id === id ? { ...n, nome, responsavel } : n) } : c));
      if (notaSelecionada?.id === id) setNotaSelecionada(prev => ({ ...prev, nome, responsavel }));
      setNotaEditData({ id: null, nome: "", responsavel: "", pilhaId: null });
    } else { alert("Erro ao salvar alterações."); }
  };

  const saveDataConclusao = async (notaId, data) => {
    const { error } = await supabase.from("notas").update({ data_conclusao: data || null }).eq("id", notaId);
    if (!error) { setDataConclusaoSalva(prev => ({ ...prev, [notaId]: data || "" })); setDataConclusaoEdit(prev => { const cp = { ...prev }; delete cp[notaId]; return cp; }); }
  };

  const saveDataEntrega = async (notaId, data) => {
    const { error } = await supabase.from("notas").update({ data_entrega: data || null }).eq("id", notaId);
    if (!error) {
      setDataEntregaSalva(prev => ({ ...prev, [notaId]: data || "" }));
      setDataEntregaEdit(prev => { const cp = { ...prev }; delete cp[notaId]; return cp; });
      const update = (setter) => setter(prev => prev.map(col => ({ ...col, notas: col.notas.map(n => n.id === notaId ? { ...n, data_entrega: data || null } : n) })));
      update(setColumnsNormais); update(setColumnsArquivadas);
    }
  };

  useEffect(() => {
    const fn = (e) => {
      Object.keys(dataConclusaoEdit).forEach(id => {
        const el = document.querySelector(`.data-conclusao-container[data-nota-id="${id}"]`);
        if (el && !el.contains(e.target)) saveDataConclusao(id, dataConclusaoEdit[id]);
      });
    };
    if (Object.keys(dataConclusaoEdit).length > 0) { document.addEventListener("mousedown", fn); return () => document.removeEventListener("mousedown", fn); }
  }, [dataConclusaoEdit]);

  const saveColumnsOrder = async (newColumns, isArquivadas) => {
    const updates = newColumns.map((col, index) => ({ id: col.id, ordem: index, title: col.title })).filter(col => col.title !== "Recebidos").map(({ id, ordem }) => ({ id, ordem }));
    try {
      await Promise.all(updates.map(({ id, ordem }) => supabase.from("pilhas").update({ ordem }).eq("id", id)));
      const setter = isArquivadas ? setColumnsArquivadas : setColumnsNormais;
      setter(prev => prev.map(col => { const updated = updates.find(u => u.id === col.id); return updated ? { ...col, ordem: updated.ordem } : col; }));
    } catch { alert("Erro ao salvar posição das pilhas."); }
  };

  const onDragEnd = useCallback(async (result) => {
    const { source, destination, type } = result;
    if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) return;
    const cols = modoArquivadas ? columnsArquivadas : columnsNormais;
    const setCols = modoArquivadas ? setColumnsArquivadas : setColumnsNormais;

    if (type === "COLUMN") {
      const newColumns = Array.from(cols);
      const [moved] = newColumns.splice(source.index, 1);
      newColumns.splice(destination.index, 0, moved);
      const recIdx = newColumns.findIndex(col => col.title === "Recebidos");
      if (recIdx > 0) { const rec = newColumns.splice(recIdx, 1)[0]; newColumns.unshift(rec); }
      setCols(newColumns);
      await saveColumnsOrder(newColumns, modoArquivadas);
      return;
    }

    if (type === "CARD") {
      const nextColumns = cols.map(c => ({ ...c, notas: [...c.notas] }));
      const sourceCol = nextColumns.find(c => c.id === source.droppableId);
      const [movedNote] = sourceCol.notas.splice(source.index, 1);
      const destCol = nextColumns.find(c => c.id === destination.droppableId);
      destCol.notas.splice(destination.index, 0, movedNote);
      setCols(nextColumns);
      try {
        for (const [idx, nota] of destCol.notas.entries()) await supabase.from("notas").update({ pilha_id: destCol.id, ordem: idx }).eq("id", nota.id);
        if (source.droppableId !== destination.droppableId) {
          for (const [idx, nota] of sourceCol.notas.entries()) await supabase.from("notas").update({ ordem: idx }).eq("id", nota.id);
        }
        if (notaSelecionada?.id === movedNote.id && TIPOS_COM_VISUALIZACAO.includes(movedNote.tipo)) {
          setNotaSelecionada(prev => ({ ...prev, pilha_id: destCol.id }));
          const destIsRecebidos = destCol.title === "Recebidos";
          setIsNotaRecebidos(destIsRecebidos);
          if (destIsRecebidos) loadOrigemData(movedNote.id);
          else { setProjetoOrigem(null); setNotaOrigem(null); }
        }
      } catch { alert("Erro ao salvar posição das notas. Atualize a página."); }
    }
  }, [columnsNormais, columnsArquivadas, modoArquivadas, notaSelecionada]);

  const renderNotaContent = (nota, onClose) => {
    if (!nota) return null;
    return (
      <ModalNota
        showNovaNota={!!activeColumnId} showEditarNota={!!notaEditData.id && !notaSelecionada}
        showVisualizarNota={!!notaSelecionada} onCloseNovaNota={() => setActiveColumnId(null)}
        onCloseEditarNota={() => setNotaEditData({ id: null, nome: "", responsavel: "", pilhaId: null })}
        onCloseVisualizarNota={handleCloseNota} formData={formData} setFormData={setFormData}
        handleSaveTask={handleSaveTask} notaEditData={notaEditData} setNotaEditData={setNotaEditData}
        saveEditedNota={saveEditedNota} notaSelecionada={notaSelecionada}
        project={{ ...entity, tipo: entity?.type === "project" ? "projeto" : "setor" }}
        usuarioAtual={usuarioAtual} usuarioId={usuarioId} notaProgresso={notaProgresso}
        setNotaProgresso={setNotaProgresso} donoContainerId={donoContainerId}
        onStatusUpdate={atualizarStatusNota} setColumnsNormais={setColumnsNormais}
        setColumnsArquivadas={setColumnsArquivadas}
        onOpenTask={handleOpenTask}
        onNotaCopiada={(notaNova) => handleOpenTask(notaNova.id)}
      />
    );
  };

  // ── Carrega as tags da entidade ──
  useEffect(() => {
    if (!entity?.id) return;
    supabase
      .from("tags")
      .select("*")
      .eq("entidade_tipo", entityType)
      .eq("entidade_id", String(entity.id))
      .order("created_at", { ascending: true })
      .then(({ data }) => setTags(data || []));
  }, [entity?.id, entityType]);

  // ── Handlers de tag ──
  const handleCreateTag = async (dados) => {
    const { data, error } = await supabase
      .from("tags")
      .insert([{ ...dados, entidade_tipo: entityType, entidade_id: String(entity.id), container_id: donoContainerId }])
      .select()
      .single();
    if (error) { alert("Erro ao criar tag."); return; }
    setTags(prev => [...prev, data]);
  };

  const sincronizarTagNasNotas = async (tagId, fn) => {
    const afetadas = [...columnsNormais, ...columnsArquivadas]
      .flatMap(c => c.notas)
      .filter(n => Array.isArray(n.tags) && n.tags.some(t => String(t.id) === String(tagId)));
    await Promise.all(afetadas.map(async (n) => {
      const novas = fn(n.tags);
      const { error } = await supabase.from("notas").update({ tags: novas }).eq("id", n.id);
      if (!error) atualizarStatusNota(n.id, { tags: novas });
    }));
  };

  const handleUpdateTag = async (id, dados) => {
    const { error } = await supabase.from("tags").update(dados).eq("id", id);
    if (error) { alert("Erro ao salvar tag."); return; }
    setTags(prev => prev.map(t => (t.id === id ? { ...t, ...dados } : t)));
    await sincronizarTagNasNotas(id, lista => lista.map(t => (String(t.id) === String(id) ? { ...t, ...dados } : t)));
  };

  const handleDeleteTag = async (id) => {
    if (!window.confirm("Excluir esta tag? Ela será removida de todas as notas.")) return;
    const { error } = await supabase.from("tags").delete().eq("id", id);
    if (error) { alert("Erro ao excluir tag."); return; }
    setTags(prev => prev.filter(t => t.id !== id));
    setFiltroTag(prev => (String(prev) === String(id) ? null : prev));
    await sincronizarTagNasNotas(id, lista => lista.filter(t => String(t.id) !== String(id)));
  };

  const handleAddTagNota = async (notaId, tagId) => {
    const tag = tags.find(t => String(t.id) === String(tagId));
    if (!tag) return;
    const nota = [...columnsNormais, ...columnsArquivadas].flatMap(c => c.notas).find(n => n.id === notaId);
    const atuais = Array.isArray(nota?.tags) ? nota.tags : [];
    if (atuais.some(t => String(t.id) === String(tag.id))) return;
    const novas = [...atuais, { id: tag.id, nome: tag.nome, cor: tag.cor, negrito: tag.negrito, italico: tag.italico }];
    const { error } = await supabase.from("notas").update({ tags: novas }).eq("id", notaId);
    if (!error) atualizarStatusNota(notaId, { tags: novas });
  };

  const handleRemoveTagNota = async (notaId, tagId) => {
    const nota = [...columnsNormais, ...columnsArquivadas].flatMap(c => c.notas).find(n => n.id === notaId);
    const novas = (Array.isArray(nota?.tags) ? nota.tags : []).filter(t => String(t.id) !== String(tagId));
    const { error } = await supabase.from("notas").update({ tags: novas }).eq("id", notaId);
    if (!error) atualizarStatusNota(notaId, { tags: novas });
  };

  if (loading) return <Loading />;

  const filteredColumns = columnsAtivas
    .filter(col => !pilhaSelecionada || col.id === pilhaSelecionada)
    .map(col => ({
      ...col,
      notas: col.notas.filter(nota => {
        const passaBusca = !searchTerm || nota.nome.toLowerCase().includes(searchTerm.toLowerCase());
        const passaPrioridade = !filtroPrioridade || nota.prioridade === filtroPrioridade;
        const passaTag = !filtroTag ||
          (Array.isArray(nota.tags) && nota.tags.some(t => String(t.id) === String(filtroTag)));
        return passaBusca && passaPrioridade && passaTag;
      }),
    }))
    .filter(col => {
      const temFiltroAtivo = filtroPrioridade || filtroTag || searchTerm;
      if (!temFiltroAtivo) return true;
      return col.notas.length > 0;
    });

  const allColumns = [...columnsNormais, ...columnsArquivadas];

  const colunaDaNotaSelecionada = (() => {
    if (!notaSelecionada) return null;
    const todasColunas = [...columnsNormais, ...columnsArquivadas];
    return todasColunas.find(c => c.notas.some(n => n.id === notaSelecionada.id)) || null;
  })();

  const notasDaPilhaSelecionada = colunaDaNotaSelecionada?.notas || [];

  return (
    <div className="cards-layout">
      <div className="cards-sidebar-wrapper">
        <CardsHeader
          entity={entity}
          membros={membros}
          donoContainerId={donoContainerId}
          onToggleArquivadas={() => setViewMode(prev => prev === "arquivadas" ? "kanban" : "arquivadas")}
          modoArquivadas={modoArquivadas}
          onToggleGraficos={() => setViewMode(prev => prev === "graficos" ? "kanban" : "graficos")}
          modoGraficos={modoGraficos}
          columnsNormais={columnsNormais}
          onToggleProjetos={() => setViewMode(prev => prev === "projetos" ? "kanban" : "projetos")}
          modoProjetos={modoProjetos}
          filtroPrioridade={filtroPrioridade}
          onSetFiltroPrioridade={setFiltroPrioridade}
          filtroTag={filtroTag}
          onSetFiltroTag={setFiltroTag}
          pilhaSelecionada={pilhaSelecionada}
          onSelectPilha={setPilhaSelecionada}
          onResetView={handleResetView}
          modoHome={modoHome}
          onToggleHome={() => setViewMode(prev => prev === "home" ? "kanban" : "home")}
          tags={tags}
          onCreateTag={handleCreateTag}
          onUpdateTag={handleUpdateTag}
          onDeleteTag={handleDeleteTag}
          ehSetor={entityType !== "project"}
        />
      </div>

      <div className="cards-layout__right">
        <CardsTopBar
          projectName={entity?.name || ""}
          onSearch={setSearchTerm}
          profile={profile}
          session={session}
          onLogout={onLogout}
          onProfileUpdate={onProfileUpdate}
          onAddColumn={() => handleCreateColumn("normal")}
          onAddDiario={() => handleCreateColumn("diario_obras")}
          onAddCompartilhada={() => {
            setSelecionadasCompartilhar([]);
            carregarEntidadesParaCompartilhar();
            setShowCompartilhadaModal(true);
          }}
          jaExisteDiario={entityType === "workspace" ? true : jaExisteDiario}
          podeCriarDiario={entityType !== "workspace"}
          containerAtual={donoContainerId}
          onOpenAgendaItem={handleOpenAgendaItem}
        />

        <TempoTracker
          usuarioId={usuarioId}
          usuarioNome={usuarioAtual}
          usuarioAvatar={profile?.avatar_url}
          entityType={entityType}
          entityId={entity?.id}
          entityNome={entity?.name}
          setorNome={null}
        />

        <div className={`cards-page${modoArquivadas ? " arquivo-cards-page" : ""}`}>
          {modoHome ? (
            <div className="cards-home-view">
              <EntityDetails
                entityType={entityType}
                entity={entity}
                onBack={() => setViewMode("kanban")}
                canEdit={true}
                containerId={donoContainerId}
                onEdit={(updated) => setEntity(updated)}
              />
            </div>
          ) : modoGraficos ? (
            <CardsGraficos entity={entity} entityType={entityType} columnsNormais={columnsNormais} />
          ) : modoProjetos ? (
            <ProjetosObra
              entity={entity}
              entityType={entityType}
              donoContainerId={donoContainerId}
              usuarioId={usuarioId}
              membros={membros}
            />
          ) : (
            <>
              {entity?.ativo === false && (
                <div className="entity-inativo-banner">
                  <span className="material-symbols-outlined">visibility_off</span>
                  {entityType === "project" ? "Projeto Inativo" : entityType === "workspace" ? "Workspace Inativo" : "Setor Inativo"}
                </div>
              )}

              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="all-columns" direction="horizontal" type="COLUMN">
                  {(provided) => (
                    <div className="cards-body" ref={provided.innerRef} {...provided.droppableProps}>
                      <button className="trocar-projeto-fab" title="Trocar de projeto/setor" onClick={() => setShowTrocarProjeto(true)}>
                        <span className="material-symbols-outlined">swap_horiz</span>
                      </button>

                      {showTrocarProjeto && (
                        <TrocarProjetoModal
                          containerId={donoContainerId}
                          currentUserId={usuarioId}
                          currentEntityId={entity?.id}
                          onClose={() => setShowTrocarProjeto(false)}
                        />
                      )}

                      {filteredColumns.map((col, index) => (
                        <Column
                          key={col.id} col={col} membros={membros} index={index} columns={allColumns}
                          notasConcluidas={notasConcluidas} notaProgresso={notaProgresso}
                          dataConclusaoEdit={dataConclusaoEdit} dataConclusaoSalva={dataConclusaoSalva}
                          showColorPicker={showColorPicker} menuOpenPilha={menuOpenPilha} menuOpenNota={menuOpenNota}
                          editingColumnId={editingColumnId} columnTitleDraft={columnTitleDraft}
                          setMenuOpenPilha={setMenuOpenPilha} setMenuOpenNota={setMenuOpenNota}
                          setActiveColumnId={setActiveColumnId} setEditingColumnId={setEditingColumnId}
                          setColumnTitleDraft={setColumnTitleDraft} setShowColorPicker={setShowColorPicker}
                          setColumns={modoArquivadas ? setColumnsArquivadas : setColumnsNormais}
                          toggleConclusaoNota={toggleConclusaoNota} setDataConclusaoEdit={setDataConclusaoEdit}
                          saveDataConclusao={saveDataConclusao} handleOpenNota={handleOpenNota}
                          handleEditNota={handleEditNota} handleDeleteNota={handleDeleteNota}
                          onSaveResponsavelRapida={handleSaveResponsavelRapida}
                          onSaveDataEntregaRapida={handleSaveDataEntregaRapida}
                          onRemoveResponsavelRapida={handleRemoveResponsavelRapida}
                          onSavePrioridade={handleSavePrioridade}
                          onAddTagNota={handleAddTagNota}
                          onRemoveTagNota={handleRemoveTagNota}
                          modoArquivadas={modoArquivadas} donoContainerId={donoContainerId}
                          usuarioId={usuarioId} entityType={entityType} entity={entity}
                          columnsNormais={columnsNormais} columnsArquivadas={columnsArquivadas}
                          dataEntregaEdit={dataEntregaEdit} dataEntregaSalva={dataEntregaSalva}
                          setDataEntregaEdit={setDataEntregaEdit} saveDataEntrega={saveDataEntrega}
                          expandedColumnId={expandedColumnId} setExpandedColumnId={setExpandedColumnId}
                          expandedNotaView={expandedNotaView} setExpandedNotaView={setExpandedNotaView}
                          renderNotaContent={renderNotaContent}
                          onToggleFlutuante={handleToggleFlutuante}
                          onOpenTask={handleOpenTask}
                          onGerenciarCompartilhamento={abrirGerenciarCompartilhamento}
                        />
                      ))}
                      {provided.placeholder}

                      <div className="floating-notas-layer">
                        {[...columnsNormais, ...columnsArquivadas]
                          .flatMap(c => c.notas.map(n => ({ ...n, __pilhaId: c.id })))
                          .filter(n => n.tipo === "Nota Rápida" && n.flutuante)
                          .map(nota => (
                            <NotaRapidaCard
                              key={nota.id}
                              nota={nota}
                              flutuante
                              onSaveResponsavel={handleSaveResponsavelRapida}
                              onRemoveResponsavel={handleRemoveResponsavelRapida}
                              handleDeleteNota={handleDeleteNota}
                              menuOpenNota={menuOpenNota}
                              setMenuOpenNota={setMenuOpenNota}
                              pilhaId={nota.__pilhaId}
                              containerId={donoContainerId}
                              usuarioId={usuarioId}
                              entityType={entityType}
                              entityId={entity?.id}
                              onToggleFlutuante={() => handleToggleFlutuante(nota, nota.__pilhaId)}
                              onSalvarPosicao={(x, y) => handleSalvarPosicaoFlutuante(nota.id, x, y)}
                              onRedimensionar={(w, h) => handleRedimensionarNotaFlutuante(nota.id, w, h)}
                              onOpenTask={handleOpenTask}
                              onSavePrioridade={handleSavePrioridade}
                              notasDisponiveis={[...columnsNormais, ...columnsArquivadas].flatMap(c => c.notas).filter(n2 => n2.id !== nota.id && n2.tipo !== "Nota Rápida")}
                            />
                          ))}
                      </div>
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </>
          )}

          {isNotaRecebidos && notaSelecionada ? (
            <div className="modal-overlay">
              <div className="modal-content listagem-espelho-modal">
                <ListagemEspelho projetoOrigem={projetoOrigem} notaOrigem={notaOrigem}
                  notaEspelhoId={notaSelecionada.id} onClose={handleCloseNota} onStatusUpdate={atualizarStatusNota} />
              </div>
            </div>
          ) : (
            <ModalNota
              showNovaNota={!!activeColumnId} showEditarNota={!!notaEditData.id && !notaSelecionada}
              showVisualizarNota={!!notaSelecionada} onCloseNovaNota={() => setActiveColumnId(null)}
              onCloseEditarNota={() => setNotaEditData({ id: null, nome: "", responsavel: "", pilhaId: null })}
              onCloseVisualizarNota={handleCloseNota} formData={formData} setFormData={setFormData}
              handleSaveTask={handleSaveTask} notaEditData={notaEditData} setNotaEditData={setNotaEditData}
              saveEditedNota={saveEditedNota} notaSelecionada={notaSelecionada}
              project={{ ...entity, tipo: entity?.type === "project" ? "projeto" : "setor" }}
              usuarioAtual={usuarioAtual} usuarioId={usuarioId} notaProgresso={notaProgresso}
              setNotaProgresso={setNotaProgresso} donoContainerId={donoContainerId}
              onStatusUpdate={atualizarStatusNota} setColumnsNormais={setColumnsNormais}
              setColumnsArquivadas={setColumnsArquivadas}
              notasDaPilha={notasDaPilhaSelecionada}
              pilhaId={colunaDaNotaSelecionada?.id}
              onOpenTask={handleOpenTask}
              onNotaCopiada={(notaNova) => handleOpenTask(notaNova.id)}
            />
          )}
        </div>
      </div>

      {/* ── Modal de criação de Pilha Compartilhada ── */}
      {showCompartilhadaModal && (
        <div className="modal-overlay" onClick={() => setShowCompartilhadaModal(false)}>
          <div className="modal-content" style={{ maxWidth: 480, background: "#ffffff" }} onClick={e => e.stopPropagation()}>
            <div className="nn-header">
              <div className="nn-header-titles">
                <h2 className="nn-title">Nova Pilha Compartilhada</h2>
                <p className="nn-subtitle">Escolha com quais projetos, setores ou workspaces esta pilha será compartilhada.</p>
              </div>
            </div>
            <div className="nn-divider" />
            <div className="nn-body" style={{ maxHeight: 320, overflowY: "auto" }}>
              {carregandoEntidadesCompartilhar ? (
                <div className="loader-container"><div className="loader"></div></div>
              ) : entidadesParaCompartilhar.length === 0 ? (
                <p className="empty">Nenhum outro projeto, setor ou workspace disponível.</p>
              ) : (
                entidadesParaCompartilhar.map(ent => {
                  const key = `${ent.tipo}-${ent.id}`;
                  const marcado = selecionadasCompartilhar.some(s => s.tipo === ent.tipo && s.id === ent.id);
                  return (
                    <label key={key} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={marcado}
                        onChange={() => setSelecionadasCompartilhar(prev =>
                          marcado
                            ? prev.filter(s => !(s.tipo === ent.tipo && s.id === ent.id))
                            : [...prev, ent]
                        )}
                      />
                      <span>{ent.nome} <em style={{ opacity: 0.6 }}>({ent.tipo === "project" ? "Projeto" : ent.tipo === "setor" ? "Setor" : "Workspace"})</em></span>
                    </label>
                  );
                })
              )}
            </div>
            <div className="nn-divider" />
            <div className="nn-footer">
              <button className="nn-btn-cancelar" onClick={() => setShowCompartilhadaModal(false)}>Cancelar</button>
              <button className="nn-btn-criar" onClick={handleCreateColumnCompartilhada}>Criar Pilha</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal de gerenciamento de compartilhamento ── */}
      {showGerenciarModal && (
        <div className="modal-overlay" onClick={() => setShowGerenciarModal(false)}>
          <div className="modal-content" style={{ maxWidth: 480, background: "#ffffff" }} onClick={e => e.stopPropagation()}>
            <div className="nn-header">
              <div className="nn-header-titles">
                <h2 className="nn-title">Gerenciar compartilhamento</h2>
                <p className="nn-subtitle">Marque os locais que terão acesso à pilha "{pilhaGerenciada?.title}".</p>
              </div>
            </div>
            <div className="nn-divider" />
            <div className="nn-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              {carregandoEntidadesCompartilhar ? (
                <div className="loader-container"><div className="loader"></div></div>
              ) : entidadesParaCompartilhar.length === 0 ? (
                <p className="empty">Nenhum outro projeto, setor ou workspace disponível.</p>
              ) : (
                entidadesParaCompartilhar.map(ent => {
                  const marcado = selecionadasGerenciar.some(s => s.tipo === ent.tipo && String(s.id) === String(ent.id));
                  return (
                    <label key={`${ent.tipo}-${ent.id}`} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={marcado}
                        onChange={() => setSelecionadasGerenciar(prev =>
                          marcado
                            ? prev.filter(s => !(s.tipo === ent.tipo && String(s.id) === String(ent.id)))
                            : [...prev, ent]
                        )}
                      />
                      <span>{ent.nome} <em style={{ opacity: 0.6 }}>({ent.tipo === "project" ? "Projeto" : ent.tipo === "setor" ? "Setor" : "Workspace"})</em></span>
                    </label>
                  );
                })
              )}
            </div>
            <div className="nn-divider" />
            <div className="nn-footer">
              <button className="nn-btn-cancelar" onClick={() => setShowGerenciarModal(false)}>Cancelar</button>
              <button className="nn-btn-criar" disabled={salvandoGerenciar} onClick={salvarGerenciarCompartilhamento}>
                {salvandoGerenciar ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}